import {
  extractRows,
  matchesCompanyGroupScope,
  matchesVesselScope,
  normalizePagedResponse,
  resolveRecordCompanyAdminId,
  resolveRecordVesselId,
} from '../../../shared/access/tenantScopeMatch'

export {extractRows, normalizePagedResponse}

export type CrewingPresentationFilters = {
  searchTerm?: string
  selectedVesselId?: number | '' | null
  selectedCrewId?: number | '' | null
  statusFilter?: string | 'All'
  /** Optional superadmin/operator UI narrowing — never auto-applied for tenant roles */
  selectedCompanyGroupId?: number | '' | null
  selectedCompanyAdminId?: number | '' | null
  allowCompanyPresentationFilters?: boolean
}

export type CrewingRowPipelineCounts = {
  rawCount: number
  normalizedCount: number
  permissionVisibleCount: number
  presentationFilteredCount: number
}

/** Rows visible when read permission is granted; backend response is authoritative. */
export function visibleCrewingRows<T>(canRead: boolean, normalizedRows: T[]): T[] {
  return canRead ? normalizedRows : []
}

export function matchesCrewingSearch(
  row: Record<string, unknown>,
  term: string,
  fields: string[]
): boolean {
  const needle = term.trim().toLowerCase()
  if (!needle) return true
  return fields.some((field) => String(row[field] ?? '').toLowerCase().includes(needle))
}

export function matchesCrewingStatus(
  status: string | undefined | null,
  filter: string | 'All' | undefined
): boolean {
  if (!filter || filter === 'All') return true
  return status === filter
}

export function matchesSelectedCrew(
  row: Record<string, unknown>,
  selectedCrewId: number | '' | null | undefined
): boolean {
  if (selectedCrewId == null || selectedCrewId === '') return true
  return Number(row.crewId) === Number(selectedCrewId)
}

/**
 * Presentation-only row narrowing. Never treats missing ownership metadata as a deny.
 * Company/group filters apply only when allowCompanyPresentationFilters is true
 * (superadmin/operator explicit dropdown selection).
 */
export function applyCrewingPresentationFilters<T>(
  rows: T[],
  filters: CrewingPresentationFilters,
  options?: {
    searchFields?: string[]
    getStatus?: (row: T) => string | undefined | null
    vesselById?: ReadonlyMap<number, any>
    extraPredicate?: (row: T) => boolean
  }
): T[] {
  return rows.filter((row) => {
    if (options?.extraPredicate && !options.extraPredicate(row)) return false

    const rowRec = row as unknown as Record<string, unknown>

    if (!matchesSelectedCrew(rowRec, filters.selectedCrewId)) return false
    if (!matchesVesselScope(rowRec, filters.selectedVesselId ?? '')) return false

    if (filters.searchTerm && options?.searchFields?.length) {
      if (!matchesCrewingSearch(rowRec, filters.searchTerm, options.searchFields)) return false
    }

    if (options?.getStatus) {
      if (!matchesCrewingStatus(options.getStatus(row), filters.statusFilter)) return false
    }

    if (filters.allowCompanyPresentationFilters) {
      if (
        filters.selectedCompanyGroupId != null &&
        filters.selectedCompanyGroupId !== '' &&
        !matchesCompanyGroupScope(
          rowRec,
          Number(filters.selectedCompanyGroupId),
          options?.vesselById as ReadonlyMap<number, Record<string, unknown>> | undefined
        )
      ) {
        return false
      }
      if (
        filters.selectedCompanyAdminId != null &&
        filters.selectedCompanyAdminId !== ''
      ) {
        const resolved = resolveRecordCompanyAdminId(
          rowRec,
          options?.vesselById as ReadonlyMap<number, Record<string, unknown>> | undefined
        )
        const target = Number(filters.selectedCompanyAdminId)
        // Explicit superadmin CA filter: CGA-only rows (null CA) must not match via group id.
        if (resolved == null || resolved !== target) {
          return false
        }
      }
    }

    return true
  })
}

export type CrewingPresentationFilterOptions = {
  searchFields?: string[]
  getStatus?: (row: any) => string | undefined | null
  vesselById?: ReadonlyMap<number, any>
  extraPredicate?: (row: any) => boolean
}

export function buildCrewingRowPipeline<T>(
  canRead: boolean,
  rawResponse: unknown,
  filters: CrewingPresentationFilters,
  options?: CrewingPresentationFilterOptions
): {rows: T[]; counts: CrewingRowPipelineCounts} {
  const normalizedRows = extractRows<T>(rawResponse)
  const permissionVisible = visibleCrewingRows(canRead, normalizedRows)
  const rows = applyCrewingPresentationFilters(permissionVisible, filters, options)
  const counts: CrewingRowPipelineCounts = {
    rawCount: Array.isArray(rawResponse) ? rawResponse.length : normalizedRows.length,
    normalizedCount: normalizedRows.length,
    permissionVisibleCount: permissionVisible.length,
    presentationFilteredCount: rows.length,
  }
  if (process.env.NODE_ENV !== 'production') {
    console.debug('[CrewingRowPipeline]', counts)
  }
  return {rows, counts}
}

export function resolveRowVesselId(row: Record<string, unknown>): number | undefined {
  return resolveRecordVesselId(row)
}

/** Superadmin/operator may optionally narrow lists via company dropdowns. */
export function allowCompanyPresentationFilters(
  isSuperAdmin: boolean,
  roleId?: number,
  isOperator?: boolean
): boolean {
  return isSuperAdmin || roleId === 1 || roleId === 6 || !!isOperator
}

